/**
 * EmailSettingsPanel — Email Provider Configuration
 *
 * CHANGE: Added smtp_ehlo_domain field inside SMTP Configuration section.
 * This is the EHLO/HELO hostname nodemailer sends during the SMTP handshake.
 * When using shared hosting (e.g. HostGator mail.peoplesdock.com), the EHLO domain
 * must match the PTR/reverse-DNS of the mail server IP — otherwise Gmail and
 * other external providers reject or spam-folder the emails.
 *
 * Usage in PlatformSettings.js:
 *   import EmailSettingsPanel from './EmailSettingsPanel';
 *   <EmailSettingsPanel />
 *
 * API endpoints:
 *   GET  /superadmin/email-settings       — load config
 *   PUT  /superadmin/email-settings       — save config
 *   POST /superadmin/email-settings/test  — send test email
 */
import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Form, Button, Badge,
    Spinner, Alert, InputGroup,
} from 'react-bootstrap';
import { Mail, Eye, EyeOff, CheckCircle, Send, Info } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

const EmailSettingsPanel = () => {
    const [loading,  setLoading]  = useState(true);
    const [saving,   setSaving]   = useState(false);
    const [testing,  setTesting]  = useState(false);
    const [testTo,   setTestTo]   = useState('');
    const [showPass, setShowPass] = useState(false);
    const [showKey,  setShowKey]  = useState(false);

    const [form, setForm] = useState({
        email_enabled:    true,
        email_provider:   'resend',
        resend_api_key:   '',
        resend_from:      '',
        smtp_host:        '',
        smtp_port:        '587',
        smtp_secure:      false,
        smtp_user:        '',
        smtp_pass:        '',
        smtp_from:        '',
        smtp_ehlo_domain: '',   // ← NEW
    });

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    // ── Load settings ─────────────────────────────────────────────────────
    useEffect(() => {
        axiosInstance.get('/superadmin/email-settings')
            .then(r => {
                const d = r.data.data || {};
                setForm(p => ({
                    ...p,
                    email_enabled:    d.email_enabled !== false,
                    email_provider:   d.email_provider   || 'resend',
                    resend_api_key:   d.resend_api_key   || '',
                    resend_from:      d.resend_from       || '',
                    smtp_host:        d.smtp_host         || '',
                    smtp_port:        d.smtp_port         || '587',
                    smtp_secure:      d.smtp_secure === 'true' || d.smtp_secure === true,
                    smtp_user:        d.smtp_user         || '',
                    smtp_pass:        d.smtp_pass         || '',
                    smtp_from:        d.smtp_from         || '',
                    smtp_ehlo_domain: d.smtp_ehlo_domain  || '',  // ← NEW
                }));
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    // ── Save ──────────────────────────────────────────────────────────────
    const save = async () => {
        setSaving(true);
        try {
            await axiosInstance.put('/superadmin/email-settings', form);
            toast.success('Email settings saved!');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save');
        } finally { setSaving(false); }
    };

    // ── Test ──────────────────────────────────────────────────────────────
    const sendTest = async () => {
        if (!testTo.trim()) { toast.error('Enter a test email address'); return; }
        setTesting(true);
        try {
            await axiosInstance.post('/superadmin/email-settings/test',
                { test_to: testTo.trim() });
            toast.success(`Test email sent to ${testTo} ✓`);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Test failed — check your credentials');
        } finally { setTesting(false); }
    };

    if (loading) return (
        <div className="text-center py-4">
            <Spinner animation="border" variant="primary" size="sm"/>
        </div>
    );

    const isResend = form.email_provider === 'resend';
    const isSMTP   = form.email_provider === 'smtp';

    return (
        <div>
            {/* ── Header ── */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h5 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <Mail size={20} className="text-primary"/>
                        Email Delivery Settings
                    </h5>
                    <p className="text-muted small mb-0">
                        Configure how PeoplesDock sends transactional emails
                        (leave approvals, payslips, notifications, etc.)
                    </p>
                </div>
                <Form.Check
                    type="switch"
                    id="email-enabled-switch"
                    label={<span className="fw-bold">{form.email_enabled ? 'Enabled' : 'Disabled'}</span>}
                    checked={!!form.email_enabled}
                    onChange={e => set('email_enabled', e.target.checked)}
                />
            </div>

            {!form.email_enabled && (
                <Alert variant="warning" className="small py-2 mb-4">
                    Email delivery is disabled. No emails will be sent from PeoplesDock.
                </Alert>
            )}

            {/* ── Provider selector ── */}
            <Row className="g-3 mb-4">
                <Col xs={12} md={6}>
                    <div
                        onClick={() => set('email_provider', 'resend')}
                        style={{
                            border: `2px solid ${isResend ? '#4f46e5' : '#e5e7eb'}`,
                            borderRadius: 12, padding: '20px 24px', cursor: 'pointer',
                            background: isResend ? '#eef2ff' : '#fff', transition: 'all 0.2s',
                        }}>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                                <div className="fw-bold">Resend</div>
                                <div className="text-muted small">Email API (recommended)</div>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                <Badge bg="success" style={{ fontSize: 10 }}>Free 3k/mo</Badge>
                                {isResend && <CheckCircle size={18} color="#4f46e5"/>}
                            </div>
                        </div>
                        <ul className="small text-muted mb-0 ps-3">
                            <li>No SMTP server needed</li>
                            <li>99.9% deliverability</li>
                            <li>Free tier: 3,000 emails/month</li>
                            <li>Get API key at resend.com</li>
                        </ul>
                    </div>
                </Col>
                <Col xs={12} md={6}>
                    <div
                        onClick={() => set('email_provider', 'smtp')}
                        style={{
                            border: `2px solid ${isSMTP ? '#4f46e5' : '#e5e7eb'}`,
                            borderRadius: 12, padding: '20px 24px', cursor: 'pointer',
                            background: isSMTP ? '#eef2ff' : '#fff', transition: 'all 0.2s',
                        }}>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                                <div className="fw-bold">SMTP</div>
                                <div className="text-muted small">Your own email server</div>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                <Badge bg="secondary" style={{ fontSize: 10 }}>Self-hosted</Badge>
                                {isSMTP && <CheckCircle size={18} color="#4f46e5"/>}
                            </div>
                        </div>
                        <ul className="small text-muted mb-0 ps-3">
                            <li>Gmail, Zoho, Outlook, any SMTP</li>
                            <li>Use your company email domain</li>
                            <li>Requires SMTP credentials</li>
                            <li>Gmail: use App Passwords</li>
                        </ul>
                    </div>
                </Col>
            </Row>

            {/* ── Resend fields ── */}
            {isResend && (
                <Card className="border-0 shadow-sm p-4 mb-4">
                    <div className="fw-bold mb-3 small text-uppercase text-muted">
                        Resend Configuration
                    </div>
                    <Row className="g-3">
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">
                                API Key <span className="text-danger">*</span>
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showKey ? 'text' : 'password'}
                                    placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxxxx"
                                    value={form.resend_api_key}
                                    onChange={e => set('resend_api_key', e.target.value)}
                                />
                                <Button variant="outline-secondary" type="button"
                                    onClick={() => setShowKey(p => !p)}>
                                    {showKey ? <EyeOff size={15}/> : <Eye size={15}/>}
                                </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">
                                Get your API key from{' '}
                                <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer">
                                    resend.com/api-keys
                                </a>
                            </Form.Text>
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">
                                From Address <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="PeoplesDock <no-reply@peoplesdock.com>"
                                value={form.resend_from}
                                onChange={e => set('resend_from', e.target.value)}
                            />
                            <Form.Text className="text-muted">
                                Must match a verified domain in Resend.
                            </Form.Text>
                        </Col>
                    </Row>
                </Card>
            )}

            {/* ── SMTP fields ── */}
            {isSMTP && (
                <Card className="border-0 shadow-sm p-4 mb-4">
                    <div className="fw-bold mb-3 small text-uppercase text-muted">
                        SMTP Configuration
                    </div>
                    <Row className="g-3">
                        {/* Host + Port */}
                        <Col xs={8}>
                            <Form.Label className="small fw-bold">
                                SMTP Host <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                placeholder="mail.peoplesdock.com"
                                value={form.smtp_host}
                                onChange={e => set('smtp_host', e.target.value)}
                            />
                        </Col>
                        <Col xs={4}>
                            <Form.Label className="small fw-bold">Port</Form.Label>
                            <Form.Select
                                value={form.smtp_port}
                                onChange={e => {
                                    set('smtp_port', e.target.value);
                                    set('smtp_secure', e.target.value === '465');
                                }}>
                                <option value="587">587 (TLS)</option>
                                <option value="465">465 (SSL)</option>
                                <option value="25">25 (Legacy)</option>
                            </Form.Select>
                        </Col>

                        {/* ── SMTP EHLO Domain — NEW FIELD ── */}
                        <Col xs={12}>
                            <Form.Label className="small fw-bold d-flex align-items-center gap-1">
                                SMTP EHLO Domain
                                <span className="badge bg-info text-dark ms-1" style={{ fontSize: 9 }}>
                                    Fixes Gmail delivery
                                </span>
                            </Form.Label>
                            <Form.Control
                                placeholder="e.g. sh009.hostgator.in  or  mail.peoplesdock.com"
                                value={form.smtp_ehlo_domain}
                                onChange={e => set('smtp_ehlo_domain', e.target.value)}
                            />
                            <Form.Text className="text-muted d-flex align-items-start gap-1 mt-1">
                                <Info size={12} className="mt-1 flex-shrink-0"/>
                                <span>
                                    The hostname your server announces to receiving mail servers (EHLO/HELO command).
                                    For HostGator use <code>sh009.hostgator.in</code>.
                                    Must match the PTR/reverse-DNS of your SMTP server IP — this fixes rejection
                                    by Gmail and external domains. Leave blank to use server hostname (often wrong on shared hosting).
                                </span>
                            </Form.Text>
                        </Col>

                        {/* Username / Password */}
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">
                                Username / Email <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="no-reply@peoplesdock.com"
                                value={form.smtp_user}
                                onChange={e => set('smtp_user', e.target.value)}
                            />
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">
                                Password / App Password <span className="text-danger">*</span>
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showPass ? 'text' : 'password'}
                                    placeholder="Gmail: use App Password, not your login password"
                                    value={form.smtp_pass}
                                    onChange={e => set('smtp_pass', e.target.value)}
                                />
                                <Button variant="outline-secondary" type="button"
                                    onClick={() => setShowPass(p => !p)}>
                                    {showPass ? <EyeOff size={15}/> : <Eye size={15}/>}
                                </Button>
                            </InputGroup>
                            {form.smtp_user?.includes('gmail') && (
                                <Form.Text className="text-muted">
                                    Gmail: enable 2FA then generate an{' '}
                                    <a href="https://myaccount.google.com/apppasswords"
                                        target="_blank" rel="noopener noreferrer">
                                        App Password
                                    </a>
                                </Form.Text>
                            )}
                        </Col>

                        {/* From Address */}
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">From Address</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="PeoplesDock <no-reply@peoplesdock.com>"
                                value={form.smtp_from}
                                onChange={e => set('smtp_from', e.target.value)}
                            />
                        </Col>
                    </Row>

                    {/* Quick presets */}
                    <div className="mt-3">
                        <div className="text-muted small mb-2">Quick presets:</div>
                        <div className="d-flex gap-2 flex-wrap">
                            {[
                                { label: 'Gmail',    host: 'smtp.gmail.com',      port: '587', ehlo: '' },
                                { label: 'Zoho',     host: 'smtp.zoho.in',        port: '587', ehlo: '' },
                                { label: 'Outlook',  host: 'smtp.office365.com',  port: '587', ehlo: '' },
                                { label: 'SendGrid', host: 'smtp.sendgrid.net',   port: '587', ehlo: '' },
                                { label: 'HostGator',host: 'mail.peoplesdock.com',    port: '587', ehlo: 'sh009.hostgator.in' },
                            ].map(p => (
                                <Button key={p.label} size="sm" variant="outline-secondary"
                                    style={{ fontSize: 11, padding: '3px 10px' }}
                                    onClick={() => {
                                        set('smtp_host', p.host);
                                        set('smtp_port', p.port);
                                        if (p.ehlo) set('smtp_ehlo_domain', p.ehlo);
                                    }}>
                                    {p.label}
                                </Button>
                            ))}
                        </div>
                    </div>
                </Card>
            )}

            {/* ── Save + Test ── */}
            <div className="d-flex gap-3 align-items-center flex-wrap">
                <Button variant="primary" onClick={save} disabled={saving}>
                    {saving
                        ? <><Spinner size="sm" className="me-2"/>Saving…</>
                        : '💾 Save Settings'}
                </Button>
                <div className="d-flex gap-2 align-items-center ms-auto">
                    <Form.Control
                        size="sm" type="email"
                        placeholder="Test email address"
                        value={testTo}
                        onChange={e => setTestTo(e.target.value)}
                        style={{ width: 220 }}
                        onKeyDown={e => e.key === 'Enter' && sendTest()}
                    />
                    <Button variant="outline-success" size="sm" onClick={sendTest} disabled={testing}>
                        {testing
                            ? <Spinner size="sm"/>
                            : <><Send size={13} className="me-1"/>Send Test</>}
                    </Button>
                </div>
            </div>

            <Alert variant="light" className="mt-4 small border py-2">
                <strong>32 email templates</strong> are configured — leave approvals, payslips,
                helpdesk tickets, WFH requests, onboarding, performance reviews, ATS notifications,
                and more. All use the active provider you select above.
            </Alert>
        </div>
    );
};

export default EmailSettingsPanel;
